import { Portal, Snackbar } from "@mui/material";
import type { NotificationsProviderSlotProps } from "@toolpad/core";
import { useSyncExternalStore } from "react";

type SnackbarTopLayerProps = NotificationsProviderSlotProps["snackbar"];

function getOpenModalDialog(): HTMLDialogElement | null {
  const modusDialogs = document.querySelectorAll<HTMLDialogElement>(
    "dialog.modus-wc-modal"
  );
  for (let index = modusDialogs.length - 1; index >= 0; index--) {
    const dialog = modusDialogs.item(index);
    if (dialog.open) {
      return dialog;
    }
  }

  const dialogs = document.querySelectorAll<HTMLDialogElement>("dialog");
  for (let index = dialogs.length - 1; index >= 0; index--) {
    const dialog = dialogs.item(index);
    if (dialog.open) {
      return dialog;
    }
  }

  return null;
}

function subscribeToDialogOpenState(onStoreChange: () => void) {
  const observer = new MutationObserver(onStoreChange);
  observer.observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ["open"],
  });

  return () => {
    observer.disconnect();
  };
}

function getSnackbarPortalContainer(): HTMLElement {
  return getOpenModalDialog() ?? document.body;
}

function useSnackbarPortalContainer(open: boolean | undefined) {
  const observedContainer = useSyncExternalStore(
    (onStoreChange) => {
      if (!open) {
        return () => undefined;
      }

      return subscribeToDialogOpenState(onStoreChange);
    },
    () => (open ? getSnackbarPortalContainer() : document.body),
    () => document.body
  );

  return observedContainer;
}

/**
 * Toolpad snackbar portaled into an open Modus <dialog> when needed so it stays
 * above modal content and remains clickable.
 */
export function SnackbarTopLayer(props: SnackbarTopLayerProps) {
  const { children, open, sx, slotProps, anchorOrigin, ...rest } = props;
  const container = useSnackbarPortalContainer(open);

  const badgeOverflowSx = {
    overflow: "visible",
    "& .MuiBadge-root": { overflow: "visible" },
  };

  return (
    <Portal container={container}>
      <Snackbar
        {...rest}
        open={open}
        anchorOrigin={anchorOrigin}
        slotProps={slotProps}
        sx={[badgeOverflowSx, ...(sx === undefined ? [] : [sx].flat())]}
      >
        {children}
      </Snackbar>
    </Portal>
  );
}
